import React from "react";
import { observer } from "mobx-react-lite";
import { Icon } from "@iconify-icon/react";
import { useStore } from "../../../stores/StoreContext";
import { Tabs, TabsList, TabsTrigger } from "../../ui/tabs";
import { getSimpleDateFormat } from "../../../Utility/Utility";

/**
 * Join a region and state for a toggle label, omitting the state when empty
 * (e.g., "San Francisco, CA" or "San Francisco")
 */
const formatRegionLabel = (region: string, state?: string | null) =>
  state ? `${region}, ${state}` : region;

const TRIGGER_CLASS =
  "rounded-md px-4 py-2 text-base font-semibold whitespace-normal text-[#1e2744] data-[state=active]:bg-[#1e2744] data-[state=active]:text-white data-[state=active]:shadow-none dark:text-slate-100 dark:data-[state=active]:bg-slate-100 dark:data-[state=active]:text-[#1e2744]";

/**
 * RegionTransferBanner Component
 * Shown on the Documents page when the user has an approved, future-dated regional
 * transfer. Lets the user switch the Upload Document type list between their
 * current region (default) and their new region. Renders nothing otherwise.
 */
const RegionTransferBanner: React.FC = observer(() => {
  const { documentStore, userStore } = useStore();
  const transfer = documentStore.regionalTransfer;

  if (!transfer) {
    return null;
  }

  return (
    <div
      role="status"
      className="mb-4 w-full rounded-lg border border-[#5b87d1] bg-[#dbe6f4] p-4 dark:border-sky-700 dark:bg-sky-950/40"
    >
      <div className="mb-3 flex items-center gap-2 text-base font-semibold text-[#3a63ad] dark:text-sky-300">
        <Icon icon="mdi:information-outline" width="22" height="22" />
        <span>
          You have an approved transfer to {transfer.newRegion} on{" "}
          {getSimpleDateFormat(transfer.expectedTransferDate)}
        </span>
      </div>

      <Tabs
        value={documentStore.isNewRegionSelected ? "new" : "current"}
        onValueChange={(value) =>
          documentStore.setNewRegionSelected(value === "new")
        }
      >
        <TabsList className="h-auto max-w-full flex-wrap justify-start gap-1 rounded-lg bg-white p-1 dark:bg-slate-800">
          <TabsTrigger value="current" className={TRIGGER_CLASS}>
            {formatRegionLabel(
              userStore.user?.Region ?? "",
              userStore.user?.State,
            )}{" "}
            (Current)
          </TabsTrigger>
          <TabsTrigger value="new" className={TRIGGER_CLASS}>
            {formatRegionLabel(transfer.newRegion, transfer.newState)} (New)
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </div>
  );
});

export default RegionTransferBanner;
